import {
  ArrowRight,
  CheckCircle2,
  CircleDollarSign,
  ExternalLink,
  MousePointerClick,
  PackagePlus,
  ShoppingBag,
  Users,
  Send,
  Sparkles,
  Store,
  TrendingUp,
  Workflow,
} from "lucide-react";
import { Link } from "react-router-dom";
import { LineChart } from "../../components/dashboard/LineChart";
import { PageHeader } from "../../components/ui/PageHeader";
import { dashboardStats, orders } from "../../data/mock";
import { formatRupiah } from "../../lib/format";
import { useAccount } from "../../context/AccountContext";

export function DashboardHomePage() {
  const { account } = useAccount();
  const sitesBase = (import.meta.env.VITE_SITES_BASE_URL || "http://localhost:8788").replace(/\/$/, "");
  const liveUrl = account?.site ? `${sitesBase}/${account.site.slug}` : sitesBase;
  const businessName = account?.site?.businessName || "Website Anda";

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={`${account?.planLabel} workspace`}
        title={`Selamat datang, ${account?.displayName || "Pengguna"}`}
        description="Kelola website, produk, dan pertumbuhan bisnis dari satu workspace. Data pada halaman ini adalah data demo untuk pengujian UI."
        actions={
          <>
            <Link to="/dashboard/builder" className="btn-secondary"><Sparkles size={17} /> Edit tampilan</Link>
            <a href={liveUrl} target="_blank" rel="noreferrer" className="btn-primary">Lihat website <ExternalLink size={17} /></a>
          </>
        }
      />

      <section className="relative isolate overflow-hidden rounded-[2.2rem] border border-emerald-900/10 bg-emerald-950 shadow-[0_28px_90px_rgba(9,55,32,.20)]">
        <div className="grid min-h-[390px] items-stretch lg:grid-cols-[.92fr_1.08fr]">
          <div className="relative z-10 flex flex-col justify-between p-7 text-white sm:p-9 lg:p-11">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-[10px] font-black uppercase tracking-[.16em] text-brand-200"><Workflow size={14} /> WhatsApp ↔ Dashboard</span>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1.5 text-[10px] font-black uppercase tracking-[.14em] text-white">Revision {account?.site?.revision || 1}</span>
              </div>
              <h2 className="mt-7 max-w-xl text-4xl font-black leading-[1.02] tracking-[-.055em] sm:text-5xl">{businessName} tetap satu, cara mengelolanya sekarang lebih lengkap.</h2>
              <p className="mt-5 max-w-xl text-sm leading-7 text-white/60">Website hasil #CSJW, nomor WhatsApp, produk, QRIS, revision, dan URL tetap terhubung. Edit cepat bisa dari chat; pengaturan lengkap dilakukan di dashboard.</p>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/dashboard/builder" className="rounded-xl bg-white px-5 py-3 text-sm font-black text-emerald-950 shadow-lg hover:-translate-y-0.5">Buka builder</Link>
              <Link to="/dashboard/products" className="rounded-xl border border-white/15 bg-white/5 px-5 py-3 text-sm font-black text-white hover:bg-white/10">Kelola produk</Link>
            </div>
          </div>
          <div className="relative min-h-[360px] overflow-hidden border-t border-white/10 lg:border-l lg:border-t-0">
            <img src="/images/dashboard-workspace.webp" alt="Pemilik usaha menggunakan dashboard CSJW" className="absolute inset-0 h-full w-full object-cover object-center" />
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-950/55 via-transparent to-transparent" />
          </div>
        </div>
      </section>

      <section className="grid overflow-hidden rounded-[1.7rem] border border-slate-200 bg-white shadow-[0_14px_50px_rgba(30,60,40,.06)] sm:grid-cols-2 xl:grid-cols-4">
        {dashboardStats.map((stat, index) => {
          const icons = [Users, MousePointerClick, ShoppingBag, CircleDollarSign];
          const Icon = icons[index] || TrendingUp;
          return (
            <article key={stat.label} className={`relative p-5 sm:p-6 ${index > 0 ? "border-t border-slate-100 sm:border-l sm:border-t-0" : ""}`}>
              <div className="flex items-start justify-between gap-3"><div><p className="text-xs font-bold text-slate-400">{stat.label}</p><p className="mt-3 text-3xl font-black tracking-[-.05em] text-slate-950">{stat.value}</p></div><span className={`grid h-10 w-10 place-items-center rounded-xl ${index === 2 ? "bg-accent-50 text-accent-600" : "bg-brand-50 text-brand-700"}`}><Icon size={18} /></span></div>
              <p className="mt-3 flex items-center gap-1.5 text-xs font-bold text-brand-700"><TrendingUp size={13} /> {stat.delta} dari periode lalu</p>
            </article>
          );
        })}
      </section>

      <section className="grid gap-5 xl:grid-cols-[1.35fr_.65fr]">
        <article className="overflow-hidden rounded-[1.9rem] border border-slate-200 bg-white shadow-[0_18px_60px_rgba(30,60,40,.07)]">
          <div className="flex flex-col gap-3 border-b border-slate-100 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
            <div><p className="text-lg font-black tracking-[-0.03em] text-slate-900">Performa website</p><p className="mt-1 text-xs font-semibold text-slate-400">Pengunjung dan aksi pembeli • data demo</p></div>
            <select className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-600"><option>12 bulan</option><option>30 hari</option><option>7 hari</option></select>
          </div>
          <div className="p-5 sm:p-6"><LineChart /></div>
        </article>

        <article className="relative overflow-hidden rounded-[1.9rem] border border-slate-200 bg-[#fff7ef] p-6 shadow-[0_18px_60px_rgba(80,45,20,.08)]">
          <div className="absolute -right-10 -top-10 h-36 w-36 rounded-full bg-accent-200/50 blur-3xl" />
          <div className="relative">
            <div className="flex items-center justify-between"><span className="grid h-11 w-11 place-items-center rounded-2xl bg-accent-500 text-white"><Store size={21} /></span><span className="badge-success"><CheckCircle2 size={13} /> {account?.site?.status || "Online"}</span></div>
            <p className="mt-8 text-xs font-black uppercase tracking-[.16em] text-accent-700">Website aktif</p>
            <h3 className="mt-2 text-2xl font-black tracking-[-.04em] text-slate-950">{businessName}</h3>
            <p className="mt-2 break-all text-sm leading-6 text-slate-500">{liveUrl}</p>
            <div className="mt-7 grid gap-2">
              <a href={liveUrl} target="_blank" rel="noreferrer" className="btn-accent">Buka website <ExternalLink size={16} /></a>
              <Link to="/dashboard/website" className="btn-secondary">Pengaturan website</Link>
            </div>
          </div>
        </article>
      </section>

      <section className="grid gap-5 xl:grid-cols-[.72fr_1.28fr]">
        <article className="rounded-[1.9rem] border border-slate-200 bg-white p-5 shadow-[0_18px_60px_rgba(30,60,40,.07)] sm:p-6">
          <div><h3 className="text-lg font-black text-slate-900">Aksi cepat</h3><p className="mt-1 text-xs font-semibold text-slate-400">Pekerjaan yang paling sering dilakukan</p></div>
          <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2">
            {[
              [PackagePlus, "Tambah produk", "/dashboard/products", "bg-brand-50 text-brand-700"],
              [Sparkles, "Edit website", "/dashboard/builder", "bg-accent-50 text-accent-700"],
              [ShoppingBag, "Kelola pesanan", "/dashboard/orders", "bg-sky-50 text-sky-700"],
              [Send, "Bagikan website", "/dashboard/website", "bg-violet-50 text-violet-700"],
            ].map(([Icon, label, to, tone]) => {
              const ActionIcon = Icon as typeof PackagePlus;
              return <Link key={label as string} to={to as string} className="group flex items-center gap-3 rounded-2xl border border-slate-200 p-3.5 hover:border-brand-200 hover:bg-brand-50/50"><span className={`grid h-10 w-10 place-items-center rounded-xl ${tone as string}`}><ActionIcon size={18} /></span><span className="text-sm font-extrabold text-slate-700 group-hover:text-brand-800">{label as string}</span><ArrowRight size={15} className="ml-auto text-slate-300 group-hover:translate-x-1 group-hover:text-brand-600" /></Link>;
            })}
          </div>
        </article>

        <article className="overflow-hidden rounded-[1.9rem] border border-slate-200 bg-white shadow-[0_18px_60px_rgba(30,60,40,.07)]">
          <div className="flex items-center justify-between border-b border-slate-100 p-5 sm:p-6"><div><h3 className="text-lg font-black text-slate-900">Pesanan terbaru</h3><p className="mt-1 text-xs font-semibold text-slate-400">Riwayat tetap berada pada akun yang sama setelah upgrade</p></div><Link to="/dashboard/orders" className="text-xs font-extrabold text-brand-700 hover:text-brand-800">Lihat semua</Link></div>
          <div className="divide-y divide-slate-100">
            {orders.slice(0, 4).map((order) => (
              <div key={order.code} className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
                <div className="flex items-center gap-3"><span className="grid h-10 w-10 place-items-center rounded-xl bg-brand-50 text-brand-700"><CircleDollarSign size={18} /></span><div><p className="text-sm font-extrabold text-slate-800">{order.buyer}</p><p className="mt-0.5 text-xs text-slate-400">{order.code} • {order.time}</p></div></div>
                <div className="flex items-center justify-between gap-5 sm:justify-end"><span className="text-sm font-black text-slate-800">{formatRupiah(order.total)}</span><span className={order.payment === "Dibayar" ? "badge-success" : "badge-warning"}>{order.payment}</span></div>
              </div>
            ))}
          </div>
        </article>
      </section>
    </div>
  );
}
